import { JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DropdownDivider,
  ListItem,
  onEnterKey,
  useWindowDimensions,
} from "@songtradr-one/components-frontend";
import { AppModuleKey, SubModule } from "@songtradr-one/shared-common";
import { getSsoClient, useAppContext } from "@songtradr-one/shared-frontend";
import { Dot } from "../../nav-bar-link/NavBarLink.Parts";
import {
  Props,
  buildUserMenuModules,
  getHiddenModuleKeys,
  useUserMenuSubscription,
} from "./UserMenuContent.Parts";
import { UserMenuItem } from "./user-menu-item/UserMenuItem";
import { UserMenuSubscription } from "./user-menu-subscription/UserMenuSubscription";

export const UserMenuContent = ({ onOpenChange, subModules }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAnyPermission, hasAllPermissions, user, currentOrg, appConfig } =
    useAppContext();
  const { isTablet } = useWindowDimensions();

  const ssoClient = getSsoClient();

  const handleSignOut = async () =>
    await ssoClient.actions.signOut(window.location.origin);

  const handleManageAccount = async () =>
    await ssoClient.redirects.manageAccount();

  const handleOpenHelpCenter = () => {
    if (appConfig.helpCenterUrl) window.open(appConfig.helpCenterUrl, "_blank");
  };

  const subscription = user?.subscription;
  const { canSubscribe, handleOpenSubscription } = useUserMenuSubscription(
    subscription,
    onOpenChange,
    hasAnyPermission,
  );

  const userMenuActions: Partial<Record<AppModuleKey, () => void>> = {
    [AppModuleKey.PROFILE]: handleManageAccount,
    [AppModuleKey.SIGN_OUT]: handleSignOut,
    [AppModuleKey.HELP_CENTER]: handleOpenHelpCenter,
  };

  const userMenuModules = buildUserMenuModules(
    hasAnyPermission,
    hasAllPermissions,
    getHiddenModuleKeys(appConfig),
  );

  const handleUserMenuItemClick = (
    module: Exclude<(typeof userMenuModules)[number], "divider">,
  ) => {
    if (userMenuActions[module.key]) userMenuActions[module.key]?.();
    else if (module.baseRoute) navigate(module.baseRoute);
    onOpenChange(false);
  };

  const menuItems: JSX.Element[] = [];
  let menuItemIndex = 0;
  let dividerIndex = 0;

  // User Info Menu Item
  menuItems.push(
    <UserMenuItem
      key={`user-menu-item-${menuItemIndex++}`}
      firstName={user?.firstName}
      lastName={user?.lastName}
      email={user?.email}
    />,
  );

  // Subscription Menu Item
  if (subscription) {
    menuItems.push(<DropdownDivider key={`divider-${dividerIndex++}`} />);
    menuItems.push(
      <UserMenuSubscription
        key={`user-menu-item-${menuItemIndex++}`}
        companyName={currentOrg?.name}
        planName={subscription.isActive ? subscription.name : null}
        manageUrl={subscription.manageUrl}
        canSubscribe={canSubscribe}
        onOpen={handleOpenSubscription}
      />,
    );
  }

  // Submodule Menu Items
  // Always rendered here on tablet and smaller (the app header only ever shows them itself on desktop)
  if (isTablet && subModules?.length) {
    menuItems.push(<DropdownDivider key={`divider-${dividerIndex++}`} />);
    subModules.forEach((subModule: SubModule) => {
      const active = location.pathname.includes(subModule.route);
      menuItems.push(
        <ListItem
          key={`user-menu-item-${menuItemIndex++}`}
          label={subModule.label}
          onClick={() => {
            navigate(subModule.route);
            onOpenChange(false);
          }}
          onKeyDown={onEnterKey(() => {
            navigate(subModule.route);
            onOpenChange(false);
          })}
          labelVariant="normal-strong"
          RightIcon={<Dot $active={active} />}
        />,
      );
    });
  }

  // Divider
  menuItems.push(<DropdownDivider key={`divider-${dividerIndex++}`} />);

  // User Module Menu Items
  userMenuModules.forEach((module) => {
    if (module === "divider") {
      menuItems.push(<DropdownDivider key={`divider-${dividerIndex++}`} />);
      return;
    }

    menuItems.push(
      <ListItem
        key={`user-menu-item-${menuItemIndex++}`}
        label={module.label}
        onClick={() => handleUserMenuItemClick(module)}
        onKeyDown={onEnterKey(() => handleUserMenuItemClick(module))}
      />,
    );
  });

  return menuItems;
};
